import { Camera } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  assignableRubbersForTable,
  canAssignRubberTable,
  championshipTablesFromSnapshot,
  rubberCodeLabel,
  rubberMatchupLabel,
  suggestedCameraTable,
  tableHasOnlineCamera,
} from "../../utils/championshipTableAssign";
import "./ChampionshipTableAssign.css";

export function ChampionshipTablePicker({
  rubber,
  snapshot,
  tables,
  disabled = false,
  busy = false,
  compact = false,
  onAssign,
}) {
  const { t } = useTranslation();
  if (!rubber) return null;
  const cameras = snapshot?.cameras || [];
  const rubbers = snapshot?.rubbers || [];
  const options = tables || championshipTablesFromSnapshot(snapshot);
  const current = Number(rubber.table_number);
  const suggested = suggestedCameraTable(rubber, snapshot, options);
  const editable = !disabled && rubber.state !== "done";

  return (
    <div
      className={`cm-table-assign${compact ? " is-compact" : ""}`}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <p className="cm-table-assign__label">
        {t("cmPlayOnTable", "Play on table")}
      </p>
      <div
        className="cm-table-assign__picks"
        role="group"
        aria-label={t("cmPlayOnTable", "Play on table")}
      >
        {options.map((n) => {
          const selected = Number(current) === n;
          const blocked = !canAssignRubberTable(rubber, n, rubbers);
          const camOn = tableHasOnlineCamera(cameras, n);
          return (
            <button
              key={n}
              type="button"
              className={`cm-chip cm-table-assign__chip ${
                selected ? "is-on" : ""
              } ${camOn ? "has-cam" : ""}`}
              disabled={busy || !editable || (blocked && !selected)}
              aria-pressed={selected}
              onClick={() => {
                if (!editable || selected || blocked) return;
                void onAssign?.(rubber.rubber_id, n);
              }}
            >
              {t("cmTable", "Table")} {n}
              {camOn ? <Camera size={12} aria-hidden /> : null}
            </button>
          );
        })}
      </div>
      {suggested ? (
        <button
          type="button"
          className="cm-table-assign__suggest"
          disabled={busy || !editable}
          onClick={() => void onAssign?.(rubber.rubber_id, suggested)}
        >
          <Camera size={12} aria-hidden />
          {t(
            "cmUseCameraOnTable",
            "Use the camera already on table {{n}}",
            { n: suggested }
          )}
        </button>
      ) : null}
    </div>
  );
}

export function ChampionshipMatchOnTable({
  tableNumber,
  snapshot,
  disabled = false,
  busy = false,
  onAssign,
}) {
  const { t } = useTranslation();
  const table = Number(tableNumber);
  if (!Number.isFinite(table) || table < 1) return null;
  const all = snapshot?.rubbers || [];
  const rows = assignableRubbersForTable(all, table);
  const hereRows = all.filter(
    (row) => Number(row.table_number) === table && row.state !== "done"
  );
  const currentHere =
    hereRows.find((row) => row.state === "live") || hereRows[0] || null;
  const moveRows = rows.filter((row) => Number(row.table_number) !== table);
  if (!currentHere && !moveRows.length) return null;
  const cameras = snapshot?.cameras || [];
  const camOn = tableHasOnlineCamera(cameras, table);

  return (
    <div className="cm-table-assign cm-table-assign--matches">
      <p className="cm-table-assign__label">
        {camOn
          ? t(
              "cmAssignMatchToCamera",
              "Put a match on table {{n}} — this camera records it",
              { n: table }
            )
          : t("cmAssignMatchToTable", "Put a match on table {{n}}", {
              n: table,
            })}
      </p>
      {currentHere ? (
        <ul className="cm-table-assign__matches" aria-label={t("cmOnThisTable", "On this table")}>
          <li>
            <button
              type="button"
              className={`cm-table-assign__match is-here${
                currentHere.state === "live" ? " is-live" : ""
              }`}
              disabled
            >
              <strong>{rubberCodeLabel(currentHere.code)}</strong>
              <span>{rubberMatchupLabel(currentHere, snapshot)}</span>
              <em>
                {currentHere.state === "live"
                  ? t("cmLive", "Live")
                  : t("cmOnThisTable", "On this table")}
              </em>
            </button>
          </li>
        </ul>
      ) : null}
      {moveRows.length ? (
        <ul className="cm-table-assign__matches">
          {moveRows.map((row) => (
            <li key={row.rubber_id}>
              <button
                type="button"
                className="cm-table-assign__match"
                disabled={busy || disabled}
                onClick={() => void onAssign?.(row.rubber_id, table)}
              >
                <strong>{rubberCodeLabel(row.code)}</strong>
                <span>{rubberMatchupLabel(row, snapshot)}</span>
                <em>{t("cmMoveHere", "Move here")}</em>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

